// types/sale-page.ts
// Type definitions for Sale Page Builder

// === Section Types ===

// === Container ===

export type ContainerSize = 'full' | 'xl' | 'lg' | 'md' | 'sm'

export const CONTAINER_SIZES: Record<ContainerSize, { label: string; maxWidth: string }> = {
  full: { label: 'เต็มจอ', maxWidth: '100%' },
  xl: { label: 'ใหญ่ (1140px)', maxWidth: '1140px' },
  lg: { label: 'ปกติ (1024px)', maxWidth: '1024px' },
  md: { label: 'กลาง (768px)', maxWidth: '768px' },
  sm: { label: 'เล็ก (640px)', maxWidth: '640px' },
}

// === Section Types ===

export interface BaseSection {
  id: string
  type: string
  visible: boolean
  container?: ContainerSize     // ควบคุม max-width ของ section
  spacing?: { top: number; bottom: number }
  /** ระยะห่างซ้าย-ขวาเฉพาะ section นี้ (px) — ไม่ตั้ง = ใช้ค่ากลางจาก
   *  theme.containerPaddingX (default 16) */
  paddingX?: number
  responsive?: {
    mobile?: { hidden?: boolean }
    tablet?: { hidden?: boolean }
  }
}

export interface HeroSection extends BaseSection {
  type: 'hero'
  variant: 'centered' | 'left-aligned' | 'split-image'
  title: string
  subtitle?: string
  backgroundImage?: string
  backgroundColor?: string
  overlayOpacity?: number
  ctaButton?: { text: string; action: 'scroll' | 'link' | 'cart'; link?: string }
}

export interface ProductGridSection extends BaseSection {
  type: 'product_grid'
  title?: string
  subtitle?: string
  productIds: string[]
  columns: { mobile: 1 | 2; tablet: 2 | 3; desktop: 3 | 4 }
  showPrice: boolean
  showStock: boolean
  showAddToCart: boolean
  cardRadius?: number   // border-radius ของกรอบสินค้า px (default 0)
  buttonRadius?: number // border-radius ของปุ่ม px (default 0)
  /** ระยะห่างรอบรูปสินค้าในการ์ด (px) — เดิมรูปชนขอบการ์ดพอดี
   *  ไม่ตั้ง = 0 ทุกด้าน (เพจเดิมหน้าตาไม่เปลี่ยน) */
  imagePadding?: { top: number; right: number; bottom: number; left: number }
}

export interface TextSection extends BaseSection {
  type: 'text'
  content: string
  alignment: 'left' | 'center' | 'right'
  maxWidth?: string
}

export interface ImageSection extends BaseSection {
  type: 'image'
  src: string
  alt?: string
  link?: string                 // คลิกรูปแล้วไปที่ URL นี้
  linkTarget?: '_blank' | '_self'
  layout: 'full-width' | 'contained' | 'side-by-side'
  images?: Array<{ src: string; alt?: string; link?: string }>
}

export interface CTASection extends BaseSection {
  type: 'cta'
  title: string
  subtitle?: string
  backgroundColor?: string
  buttonText: string
  buttonColor?: string
  buttonAction: 'chat_facebook' | 'chat_line' | 'link' | 'scroll_top'
  buttonLink?: string
}

export interface CountdownSection extends BaseSection {
  type: 'countdown'
  title: string
  endDate: string
  expiredText?: string
  backgroundColor?: string
}

export interface TestimonialSection extends BaseSection {
  type: 'testimonial'
  title?: string
  items: Array<{
    name: string
    avatar?: string
    text: string
    rating?: number
  }>
}

export interface FAQSection extends BaseSection {
  type: 'faq'
  title?: string
  items: Array<{ question: string; answer: string }>
}

export interface VideoSection extends BaseSection {
  type: 'video'
  url: string
  autoplay?: boolean
}

export interface DividerSection extends BaseSection {
  type: 'divider'
  style: 'line' | 'space' | 'dots'
}

export interface ImageSliderSection extends BaseSection {
  type: 'image_slider'
  title?: string
  slides: Array<{
    src: string
    alt?: string
    link?: string          // คลิกแล้วไปที่ไหน
    caption?: string
  }>
  autoplay: boolean
  autoplayInterval: number  // ms (default 4000)
  showDots: boolean
  showArrows: boolean
  height?: string           // e.g. '400px', '50vh'
}

// Free-form grid of cards. Each cell can carry an image, title,
// description, and an optional button — admins use it for feature
// callouts, gallery thumbnails, team grids, etc. Columns are set
// per breakpoint so a 5-up desktop grid can stack to 2 on tablet
// and 1 on mobile without the cells turning into postage stamps.
export interface GridSection extends BaseSection {
  type: 'grid'
  title?: string
  subtitle?: string
  columns: { mobile: 1 | 2 | 3; tablet: 1 | 2 | 3 | 4; desktop: 1 | 2 | 3 | 4 | 5 | 6 }
  gap: 'sm' | 'md' | 'lg'
  items: Array<{
    image?: string
    title?: string
    description?: string
    buttonText?: string
    buttonLink?: string
  }>
  cardRadius?: number      // px
  cardBackground?: string  // hex
}

export interface PopupSection extends BaseSection {
  type: 'popup'
  title: string
  content: string             // Rich text HTML
  image?: string
  trigger: 'on_load' | 'delay' | 'exit_intent' | 'scroll'
  delayMs?: number            // for 'delay' trigger (default 3000)
  scrollPercent?: number      // for 'scroll' trigger (e.g. 50 = 50%)
  showOnce: boolean           // แสดงครั้งเดียวต่อ session
  ctaButton?: {
    text: string
    action: 'close' | 'link' | 'chat_facebook' | 'chat_line'
    link?: string
  }
  backgroundColor?: string
  overlayClose: boolean       // คลิกนอก popup แล้วปิด
}

export type SalePageSection =
  | HeroSection
  | ProductGridSection
  | TextSection
  | ImageSection
  | CTASection
  | CountdownSection
  | TestimonialSection
  | FAQSection
  | VideoSection
  | DividerSection
  | ImageSliderSection
  | PopupSection
  | GridSection

// === Header / Footer ===

export interface PageHeader {
  enabled: boolean
  logo?: string
  showLogo?: boolean
  shopName?: string
  showShopName: boolean
  backgroundColor?: string
  textColor?: string
  sticky: boolean
  showCart: boolean
  cartButtonColor?: string   // สีปุ่มตะกร้า (default: #2563EB)
  /** Which SVG in /public/icons/carts/ to use for the cart button.
   *  Values: 'cart-01' … 'cart-06'. Defaults to 'cart-01'. The icon
   *  is rendered as a CSS mask so the white-on-coloured-button look
   *  works regardless of the SVG's internal stroke colour. */
  cartIcon?: 'cart-01' | 'cart-02' | 'cart-03' | 'cart-04' | 'cart-05' | 'cart-06'
  navLinks?: Array<{ label: string; href: string }>
}

export interface PageFooter {
  enabled: boolean
  text?: string
  backgroundColor?: string
  textColor?: string
  socialLinks?: {
    facebook?: string
    line?: string
    instagram?: string
    phone?: string
  }
}

// === Theme ===

export interface PageTheme {
  primaryColor: string
  secondaryColor: string
  backgroundColor: string
  fontFamily: string
  borderRadius: 'none' | 'sm' | 'md' | 'lg' | 'full'
  defaultContainer: ContainerSize  // default container สำหรับทุก section
  /** ระยะห่างซ้าย-ขวาของ container ทั้งเพจ (px) — กันเนื้อหาชนขอบจอบนมือถือ
   *  ไม่ตั้ง = 16px (ค่าเดิมที่ hardcode ไว้เป็น px-4) */
  containerPaddingX?: number
}

// === Tracking ===

export interface PageTracking {
  fbPixelId?: string            // Facebook Pixel ID e.g. "123456789"
  gaId?: string                 // Google Analytics 4 ID e.g. "G-XXXXXXXXXX"
  gtmId?: string                // Google Tag Manager ID e.g. "GTM-XXXXXXX"
  /** โค้ดยืนยันโดเมนของ Meta (Business Manager → Brand Safety → Domains)
   *  เก็บเฉพาะค่า content เช่น "uj64lnrhhdebv2eoyhe4rydlaq0hgg" — renderer
   *  แปะเป็น <meta name="facebook-domain-verification"> ให้เอง. จำเป็นเมื่อร้าน
   *  ใช้โดเมนตัวเองกับเซลเพจแล้วต้องยืนยันโดเมนเพื่อยิงโฆษณา/ตั้ง event */
  fbDomainVerification?: string
}

// === SEO ===

export interface PageSEO {
  metaTitle?: string            // Override page title for SEO
  metaDescription?: string      // Meta description
  canonicalUrl?: string         // Canonical URL
  ogTitle?: string              // Open Graph title
  ogDescription?: string        // Open Graph description
  ogImage?: string              // Open Graph image
  noIndex?: boolean             // Prevent search engine indexing
  /** ไอคอนที่โชว์บนแท็บเบราว์เซอร์ (favicon) — เก็บเป็น S3 key เหมือนรูปอื่น
   *  ไม่ตั้ง = ใช้ favicon ของระบบ */
  favicon?: string
}

// === Thank You Page ===

export interface ThankYouPageConfig {
  title?: string                // "ขอบคุณสำหรับคำสั่งซื้อ!"
  message?: string              // Rich text / HTML
  image?: string                // รูปภาพ/GIF
  showOrderSummary: boolean     // แสดงสรุปออเดอร์
  showBankInfo: boolean         // แสดงข้อมูลธนาคาร (สำหรับ TRANSFER)
  showSocialLinks: boolean      // แสดงลิงก์ social
  ctaText?: string              // "กลับหน้าร้าน" / "ดูสินค้าเพิ่ม"
  ctaLink?: string              // URL ปุ่ม CTA
  backgroundColor?: string
}

export const DEFAULT_THANKYOU: ThankYouPageConfig = {
  title: 'ขอบคุณสำหรับคำสั่งซื้อ!',
  message: 'เราได้รับคำสั่งซื้อของคุณแล้ว จะจัดส่งให้เร็วที่สุดค่ะ',
  showOrderSummary: true,
  showBankInfo: true,
  showSocialLinks: true,
  ctaText: 'กลับหน้าร้าน',
}

// === Cookie Consent ===

export interface CookieConsentConfig {
  enabled: boolean
  message?: string               // ข้อความหลัก
  privacyPolicyUrl?: string      // ลิงก์นโยบายความเป็นส่วนตัว
  position: 'bottom' | 'bottom-left' | 'bottom-right'
  acceptText?: string            // ข้อความปุ่มยอมรับ
  declineText?: string           // ข้อความปุ่มปฏิเสธ
  showDecline: boolean           // แสดงปุ่มปฏิเสธ
  backgroundColor?: string
  textColor?: string
  buttonColor?: string
}

export const DEFAULT_COOKIE_CONSENT: CookieConsentConfig = {
  enabled: false,
  message: 'เว็บไซต์นี้ใช้คุกกี้เพื่อปรับปรุงประสบการณ์การใช้งานและวิเคราะห์การเข้าชม',
  position: 'bottom',
  acceptText: 'ยอมรับทั้งหมด',
  declineText: 'ปฏิเสธ',
  showDecline: true,
}

// === Google Fonts Available ===

export const GOOGLE_FONTS = [
  // Thai fonts
  { name: 'Prompt', label: 'Prompt (ไทย)', category: 'thai' },
  { name: 'Sarabun', label: 'Sarabun (ไทย)', category: 'thai' },
  { name: 'Kanit', label: 'Kanit (ไทย)', category: 'thai' },
  { name: 'Noto Sans Thai', label: 'Noto Sans Thai (ไทย)', category: 'thai' },
  { name: 'IBM Plex Sans Thai', label: 'IBM Plex Sans Thai (ไทย)', category: 'thai' },
  { name: 'Mitr', label: 'Mitr (ไทย)', category: 'thai' },
  { name: 'Charm', label: 'Charm (ไทย)', category: 'thai' },
  { name: 'K2D', label: 'K2D (ไทย)', category: 'thai' },
  { name: 'Bai Jamjuree', label: 'Bai Jamjuree (ไทย)', category: 'thai' },
  { name: 'Pridi', label: 'Pridi (ไทย)', category: 'thai' },
  // Latin fonts
  { name: 'Inter', label: 'Inter', category: 'latin' },
  { name: 'Roboto', label: 'Roboto', category: 'latin' },
  { name: 'Open Sans', label: 'Open Sans', category: 'latin' },
  { name: 'Poppins', label: 'Poppins', category: 'latin' },
  { name: 'Montserrat', label: 'Montserrat', category: 'latin' },
  { name: 'Lato', label: 'Lato', category: 'latin' },
] as const

// === Cart ===

export interface CartItem {
  productId: string
  name: string
  price: number
  image?: string
  quantity: number
}

// === Block Registry (for builder sidebar) ===

export interface BlockDefinition {
  type: string
  label: string
  icon: string
  defaultData: Partial<SalePageSection>
}

export const BLOCK_DEFINITIONS: BlockDefinition[] = [
  { type: 'hero', label: 'Hero Banner', icon: 'Image', defaultData: { type: 'hero', variant: 'centered', title: 'หัวข้อหลัก', visible: true } },
  { type: 'product_grid', label: 'สินค้า', icon: 'Grid', defaultData: { type: 'product_grid', productIds: [], columns: { mobile: 2, tablet: 3, desktop: 4 }, showPrice: true, showStock: false, showAddToCart: true, cardRadius: 0, buttonRadius: 0, visible: true } },
  { type: 'text', label: 'ข้อความ', icon: 'Type', defaultData: { type: 'text', content: '<p>ข้อความ...</p>', alignment: 'left', visible: true } },
  { type: 'image', label: 'รูปภาพ', icon: 'ImageIcon', defaultData: { type: 'image', src: '', layout: 'full-width', container: 'full', visible: true } },
  { type: 'cta', label: 'ปุ่ม CTA', icon: 'MousePointer', defaultData: { type: 'cta', title: 'สนใจสั่งซื้อ?', buttonText: 'สั่งเลย', buttonAction: 'chat_facebook', visible: true } },
  { type: 'countdown', label: 'นับถอยหลัง', icon: 'Clock', defaultData: { type: 'countdown', title: 'โปรโมชั่นหมดใน', endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), visible: true } },
  { type: 'testimonial', label: 'รีวิว', icon: 'MessageSquare', defaultData: { type: 'testimonial', items: [{ name: 'ลูกค้า', text: 'สินค้าดีมากค่ะ', rating: 5 }], visible: true } },
  { type: 'faq', label: 'คำถามที่พบบ่อย', icon: 'HelpCircle', defaultData: { type: 'faq', items: [{ question: 'คำถาม?', answer: 'คำตอบ' }], visible: true } },
  { type: 'video', label: 'วิดีโอ', icon: 'Play', defaultData: { type: 'video', url: '', visible: true } },
  { type: 'divider', label: 'เส้นคั่น', icon: 'Minus', defaultData: { type: 'divider', style: 'line', visible: true } },
  { type: 'image_slider', label: 'สไลด์รูปภาพ', icon: 'Layers', defaultData: { type: 'image_slider', slides: [{ src: '', alt: 'Slide 1' }], autoplay: true, autoplayInterval: 4000, showDots: true, showArrows: true, container: 'full', visible: true } },
  { type: 'popup', label: 'Popup', icon: 'Maximize', defaultData: { type: 'popup', title: 'โปรโมชั่นพิเศษ!', content: '<p>รายละเอียด...</p>', trigger: 'delay', delayMs: 3000, showOnce: true, overlayClose: true, visible: true } },
  { type: 'grid', label: 'Grid (ตาราง)', icon: 'LayoutGrid', defaultData: { type: 'grid', columns: { mobile: 1, tablet: 2, desktop: 3 }, gap: 'md', items: [{}, {}, {}], cardRadius: 0, visible: true } },
]

// === Default Theme ===

export const DEFAULT_THEME: PageTheme = {
  primaryColor: '#3B82F6',
  secondaryColor: '#8B5CF6',
  backgroundColor: '#FFFFFF',
  fontFamily: 'Prompt',
  borderRadius: 'md',
  defaultContainer: 'xl',
}

export const DEFAULT_HEADER: PageHeader = {
  enabled: true,
  showShopName: true,
  sticky: true,
  showCart: true,
  backgroundColor: '#FFFFFF',
  textColor: '#1F2937',
  cartButtonColor: '#2563EB',
}

export const DEFAULT_FOOTER: PageFooter = {
  enabled: true,
  backgroundColor: '#1F2937',
  textColor: '#F3F4F6',
}
